import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, ApiError } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GROUP_PRIZES, SINGLE_PRIZE } from "@/lib/constants";
import { shareInvite } from "@/lib/share";
import logoEnglputzeder from "@/assets/logo-englputzeder.png";
import vereineHero from "@/assets/vereine-hero.png";
import wmTrikotIcon from "@/assets/wm-trikot-icon.png";
import { toast } from "sonner";
import { Trophy, Users, Sparkles, ArrowRight, CheckCircle2, MessageCircle, Shirt } from "lucide-react";

export default function Vereine() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { document.title = "Für Vereine – 5.000 € für eure Kasse · Englputzeder Tippspiel"; }, []);

  const shareWA = () => {
    const text = `⚽ 5.000 € für eure Vereinskasse!\nTippt gemeinsam die Fußball-WM 2026 bei Englputzeder.\n\n${window.location.origin}/vereine`;
    shareInvite(text, "Englputzeder · Vereine");
  };

  const valid = /\S+@\S+\.\S+/.test(email);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await api.auth.requestMagic({ email, purpose: "signup", redirect: "/onboarding" });
      setSent(true);
      toast.success("Magic Link gesendet 📬");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Senden");
    } finally {
      setBusy(false);
    }
  };

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* HERO */}
      <section className="relative overflow-hidden bg-hero text-primary-foreground">
        <div
          aria-hidden
          className="absolute inset-0 opacity-40"
          style={{ backgroundImage: `url(${vereineHero})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-hero/60 via-hero/70 to-hero/95" />
        <div className="relative max-w-4xl mx-auto px-4 pt-6 pb-10 sm:px-5 sm:pt-8 sm:pb-12 md:pt-14 md:pb-20">
          <div className="inline-flex items-center gap-2 mb-5 rounded-pill bg-background/95 backdrop-blur px-3 py-1.5 shadow-card">
            <img src={logoEnglputzeder} alt="Englputzeder" className="h-7 sm:h-8 w-auto" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-foreground">Tippspiel · WM 2026</span>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="space-y-4 sm:space-y-5"
          >
            <span className="inline-flex items-center gap-1.5 rounded-pill bg-gold text-foreground font-bold text-[11px] sm:text-xs px-3 py-1.5 shadow-pop">
              <Sparkles className="h-3.5 w-3.5" /> Für Vereine, Mannschaften & Stammtische
            </span>
            <h1 className="font-display font-extrabold text-[2rem] leading-[1.1] sm:text-5xl md:text-6xl sm:leading-[1.05]">
              5.000 € für<br />
              <span className="bg-accent text-accent-foreground px-2 py-0.5 rounded-md inline-block">eure Vereinskasse.</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl opacity-90 max-w-2xl">
              Tippt gemeinsam die Fußball-WM 2026 — wer als Tippgruppe die meisten Punkte holt,
              gewinnt bis zu <b>3.000 € Sponsoring</b> für den Verein.
            </p>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 }}
              className="bg-card text-card-foreground rounded-lg shadow-pop p-5 md:p-6 max-w-md"
            >
              {user ? (
                <div className="space-y-3 text-center">
                  <p className="font-display font-extrabold text-lg">Du bist schon dabei 🎉</p>
                  <p className="text-sm text-muted-foreground">
                    Hilf mit, die Aktion bekannt zu machen — teile sie mit deinem Verein.
                  </p>
                  <Button
                    onClick={shareWA}
                    className="w-full h-12 bg-[#25D366] text-white hover:bg-[#1ebe57] font-bold text-base tap"
                  >
                    <MessageCircle className="h-5 w-5 mr-1.5" /> Per WhatsApp teilen
                  </Button>
                  <Button asChild variant="outline" className="w-full h-11 font-bold tap">
                    <Link to="/">Zur Tippübersicht</Link>
                  </Button>
                </div>
              ) : sent ? (
                <div className="text-center py-2">
                  <div className="text-5xl mb-2">📬</div>
                  <p className="font-display font-extrabold text-xl">Fast geschafft!</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Magic Link gesendet an<br /><b className="text-foreground break-all">{email}</b>
                  </p>
                  <p className="text-xs text-muted-foreground mt-3">Auch im Spam-Ordner schauen.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Jetzt kostenlos starten
                    </label>
                    <Input
                      type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="trainer@deinverein.at"
                      className="mt-1 h-12 text-base"
                    />
                  </div>
                  <Button
                    type="submit" disabled={busy || !valid}
                    className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base tap"
                  >
                    {busy ? "Sende …" : <>Jetzt registrieren <ArrowRight className="h-4 w-4 ml-1" /></>}
                  </Button>
                  <div className="flex items-center gap-2.5 rounded-md bg-accent/10 border border-accent/30 p-2.5">
                    <img src={wmTrikotIcon} alt="" className="h-9 w-9 object-contain shrink-0" />
                    <div className="text-[12px] leading-snug text-foreground">
                      <b>Gratis WM-Trikot</b> für jede Anmeldung<br />
                      <span className="text-muted-foreground">+ 50 € Englputzeder-Gutschein</span>
                    </div>
                  </div>
                  <Button
                    type="button" variant="outline" onClick={shareWA}
                    className="w-full h-11 font-bold tap border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
                  >
                    <MessageCircle className="h-4 w-4 mr-1.5" /> An Verein per WhatsApp senden
                  </Button>
                  <p className="text-[11px] text-muted-foreground text-center">
                    100% kostenlos · in 30 Sekunden startklar
                  </p>
                </form>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      
      {/* PRIZES */}
      <section className="max-w-4xl mx-auto px-5 pt-10 md:pt-14 pb-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-accent mb-2">
            <Trophy className="h-4 w-4" /> Die Preise
          </div>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl">
            5.000 € Sponsoring für die besten Tippgruppen
          </h2>
          <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
            Die Preise gehen an die <b>Gruppe als Ganzes</b> — perfekt für neue Trikots,
            das Sommerfest oder die Jugendförderung.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          {GROUP_PRIZES.map((p, i) => (
            <motion.div
              key={p.rank}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className={`rounded-lg p-5 text-center shadow-card ${
                p.tone === "gold" ? "bg-gold text-foreground ring-4 ring-gold/40 sm:scale-105" :
                p.tone === "silver" ? "bg-muted text-foreground" :
                "bg-card text-card-foreground"
              }`}
            >
              <div className="text-5xl mb-2">{p.icon}</div>
              <div className="text-xs font-bold uppercase tracking-wider opacity-70">{p.rank}. Platz</div>
              <div className="font-display font-extrabold text-3xl mt-1">{p.short}</div>
              <div className="text-xs opacity-70 mt-1">Sponsoring für die Tippgruppe</div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-6 rounded-lg bg-card shadow-card p-5 flex items-center gap-4"
        >
          <div className="text-5xl shrink-0">{SINGLE_PRIZE.icon}</div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-accent">Einzel-Hauptpreis</div>
            <div className="font-display font-bold text-lg">{SINGLE_PRIZE.title}</div>
            <div className="text-xs text-muted-foreground">{SINGLE_PRIZE.hotel} · {SINGLE_PRIZE.location}</div>
          </div>
        </motion.div>

        {/* CTA inside PRIZES */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-6 rounded-lg bg-card shadow-pop border-2 border-accent/30 p-5 text-center"
        >
          <p className="font-display font-extrabold text-lg sm:text-xl">
            Sichert euch jetzt eure Chance auf <span className="text-accent">3.000 €</span>
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
            {user ? (
              <Button asChild className="h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base px-6 tap">
                <Link to="/">Zur Tippübersicht <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            ) : (
              <Button onClick={scrollTop} className="h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base px-6 tap">
                Jetzt Vereinsgruppe starten <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            <Button onClick={shareWA} className="h-12 bg-[#25D366] text-white hover:bg-[#1ebe57] font-bold text-base px-6 tap">
              <MessageCircle className="h-5 w-5 mr-1.5" /> An Verein per WhatsApp senden
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">100% kostenlos · in 30 Sekunden startklar</p>
        </motion.div>
      </section>


      {/* MEMBER BONUS */}
      <section className="max-w-4xl mx-auto px-5 py-10 md:py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="rounded-lg bg-card shadow-pop border-2 border-accent/40 p-4 sm:p-5"
        >
          <div className="flex items-center gap-4">
            <img
              src={wmTrikotIcon}
              alt="Englputzeder WM-Trikot"
              className="h-20 sm:h-24 w-auto object-contain shrink-0"
            />
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-accent">
                <Shirt className="h-3.5 w-3.5" /> Bonus für jedes Mitglied
              </div>
              <h3 className="font-display font-extrabold text-lg sm:text-2xl leading-tight mt-1">
                Jeder bekommt ein gratis Englputzeder WM-Trikot
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 leading-relaxed">
                Plus <b className="text-foreground">50 € Gutschein</b> — abholbereit bei Englputzeder.
                Je mehr Vereinsmitglieder mitmachen, desto mehr Trikots & Gutscheine landen bei euch.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            {user ? (
              <Button asChild className="h-12 flex-1 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base tap">
                <Link to="/"><Shirt className="h-4 w-4 mr-1.5" /> Zur Tippübersicht <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            ) : (
              <Button onClick={scrollTop} className="h-12 flex-1 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base tap">
                <Shirt className="h-4 w-4 mr-1.5" /> Jetzt anmelden & Trikot sichern <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            <Button
              variant="outline" onClick={shareWA}
              className="h-12 sm:flex-none font-bold tap border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
            >
              <MessageCircle className="h-4 w-4 mr-1.5" /> Per WhatsApp teilen
            </Button>
          </div>
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-muted/30 py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-5">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-accent mb-2">
              <Users className="h-4 w-4" /> So einfach geht's
            </div>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl">
              In 3 Schritten zur Vereinskasse
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              { n: 1, t: "Registrieren", d: "E-Mail eingeben, Magic Link klicken — fertig. Kein Passwort, keine App." },
              { n: 2, t: "Vereinsgruppe gründen", d: "Eigene Tippgruppe anlegen, Logo & Name setzen, WhatsApp-Link teilen." },
              { n: 3, t: "Gemeinsam tippen", d: "Je mehr Mitglieder, desto mehr Punkte. Top 3 Gruppen gewinnen." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-card rounded-lg shadow-card p-5"
              >
                <div className="h-10 w-10 rounded-full bg-accent text-accent-foreground font-display font-extrabold text-xl flex items-center justify-center shadow-pop">
                  {s.n}
                </div>
                <h3 className="font-display font-bold text-lg mt-3">{s.t}</h3>
                <p className="text-sm text-muted-foreground mt-1">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EMOTIONAL PITCH */}
      <section className="max-w-3xl mx-auto px-5 py-12 md:py-16 text-center">
        <h2 className="font-display font-extrabold text-3xl md:text-4xl leading-tight">
          Was würdet ihr<br />
          <span className="text-accent">mit 3.000 € machen?</span>
        </h2>
        <ul className="mt-6 space-y-2 text-left max-w-md mx-auto">
          {[
            "Neue Trikotgarnitur für die Kampfmannschaft",
            "Sommerfest mit Familien & Sponsoren",
            "Trainingsmaterial für die Jugend",
            "Endlich der Mannschaftsbus zum Auswärtsspiel",
          ].map((it, i) => (
            <li key={i} className="flex items-start gap-2 text-base">
              <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
        <p className="text-muted-foreground mt-6 italic">
          Der Schlüssel: <b className="text-foreground">möglichst viele aus eurem Verein</b> in die Tippgruppe holen.
          Jeder Tipp zählt für euch.
        </p>
      </section>

      {/* FINAL CTA */}
      <section className="bg-hero text-primary-foreground">
        <div className="max-w-3xl mx-auto px-5 py-12 md:py-16 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl">
            Bereit für die Vereinskasse?
          </h2>
          <p className="opacity-90 mt-2 max-w-xl mx-auto">
            Anmeldung dauert 30 Sekunden. Start frei zum Eröffnungsspiel der WM 2026.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={scrollTop} className="h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base px-8 tap">
              Jetzt Vereinsgruppe starten <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button
              onClick={shareWA}
              className="h-12 bg-[#25D366] text-white hover:bg-[#1ebe57] font-bold text-base px-8 tap"
            >
              <MessageCircle className="h-5 w-5 mr-1.5" /> Per WhatsApp teilen
            </Button>
            {!user && (
              <Button asChild variant="outline" className="h-12 font-bold text-base px-8 bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10">
                <Link to="/login">Bereits registriert? Anmelden</Link>
              </Button>
            )}
          </div>
        </div>
      </section>

      <footer className="text-center text-xs text-muted-foreground py-6 space-y-2">
        <div className="text-[11px] px-4 max-w-xl mx-auto">
          Diese Aktion steht in keinerlei Verbindung mit der FIFA oder offiziellen Sponsoren der FIFA Fußball-Weltmeisterschaft 2026.
        </div>
        <div>Powered by Englputzeder · englputzeder.at</div>
        <div className="flex justify-center gap-4">
          <Link to="/impressum" className="underline hover:text-foreground">Impressum</Link>
          <Link to="/datenschutz" className="underline hover:text-foreground">Datenschutz</Link>
          <Link to="/teilnahmebedingungen" className="underline hover:text-foreground">Teilnahmebedingungen</Link>
        </div>
      </footer>

      {/* Sticky WhatsApp FAB (mobile) */}
      <button
        onClick={shareWA}
        aria-label="Per WhatsApp teilen"
        className="md:hidden fixed bottom-4 right-4 z-40 h-14 w-14 rounded-full bg-[#25D366] text-white shadow-pop flex items-center justify-center active:scale-95 transition tap"
      >
        <MessageCircle className="h-7 w-7" />
      </button>
    </div>
  );
}
